import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, ShoppingCart, User, Package, Building2, ChevronUp, Check, Plus, X, Calendar, MessageSquare, TrendingUp, Loader2 } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartCount } = useCart();
  const { user, businesses, activeBusiness, switchBusiness, refreshUser } = useAuth();
  const cartCount = getCartCount();
  const [showBusinessSheet, setShowBusinessSheet] = useState(false);
  const [activity, setActivity] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const hasBusiness = businesses && businesses.length > 0;

  // Fetch activity when sheet opens
  useEffect(() => {
    if (showBusinessSheet && hasBusiness) {
      fetchActivity();
    }
  }, [showBusinessSheet, hasBusiness]);

  const fetchActivity = async () => {
    setLoadingActivity(true);
    try {
      const response = await axios.get(`${API}/user/businesses/activity?limit=5`, {
        withCredentials: true
      });
      setActivity(response.data.activity || []);
      setStats(response.data.stats || null);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleSwitchBusiness = async (businessId) => {
    const result = await switchBusiness(businessId);
    if (result) {
      await refreshUser();
      setShowBusinessSheet(false);
      window.location.reload();
    }
  };

  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Search, label: 'Browse', path: '/browse' },
    { icon: Package, label: 'Equipment', path: '/equipment' },
    { 
      icon: ShoppingCart, 
      label: 'Cart', 
      path: '/cart',
      badge: cartCount > 0 ? cartCount : null
    },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const currentBusiness = activeBusiness || (businesses && businesses[0]);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'completed':
        return 'text-green-400';
      case 'pending':
      case 'unread':
        return 'text-yellow-400';
      case 'cancelled':
      case 'rejected':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <>
      {/* Business Switcher Bottom Sheet */}
      {showBusinessSheet && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-business-sheet">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowBusinessSheet(false)}
          />
          
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 rounded-t-2xl max-h-[85vh] overflow-hidden animate-slide-up">
            {/* Handle */}
            <div className="flex justify-center py-2">
              <div className="w-10 h-1 bg-gray-600 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-700">
              <div>
                <h3 className="text-lg font-bold text-white">Your Businesses</h3>
                <p className="text-xs text-gray-400">{businesses?.length || 0} business{businesses?.length !== 1 ? 'es' : ''}</p>
              </div>
              <button 
                onClick={() => setShowBusinessSheet(false)}
                className="p-2 text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[65vh]">
              {/* Activity Stats */}
              {stats && (
                <div className="p-4 border-b border-gray-700">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Quick Stats</p>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-gray-700/50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-white">{stats.pending_bookings}</p>
                      <p className="text-[10px] text-yellow-400">Pending</p>
                    </div>
                    <div className="bg-gray-700/50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-white">{stats.total_bookings}</p>
                      <p className="text-[10px] text-green-400">Confirmed</p>
                    </div>
                    <div className="bg-gray-700/50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-white">{stats.new_inquiries}</p>
                      <p className="text-[10px] text-blue-400">Inquiries</p>
                    </div>
                    <div className="bg-gray-700/50 rounded-lg p-2 text-center">
                      <p className="text-sm font-bold text-white">{formatCurrency(stats.total_revenue).replace('ZAR', 'R')}</p>
                      <p className="text-[10px] text-gray-400">Revenue</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div className="p-4 border-b border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Recent Activity</p>
                  {loadingActivity && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                </div>
                
                {activity.length > 0 ? (
                  <div className="space-y-2">
                    {activity.map((item, index) => (
                      <div 
                        key={`${item.type}-${item.id}-${index}`}
                        className="flex items-start gap-3 p-2 bg-gray-700/30 rounded-lg"
                        data-testid={`activity-item-${index}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item.type === 'booking' ? 'bg-green-500/20' : 'bg-blue-500/20'
                        }`}>
                          {item.type === 'booking' ? (
                            <Calendar className={`h-4 w-4 ${item.type === 'booking' ? 'text-green-400' : 'text-blue-400'}`} />
                          ) : (
                            <MessageSquare className="h-4 w-4 text-blue-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm text-white font-medium truncate">{item.title}</p>
                            <span className={`text-[10px] capitalize ${getStatusColor(item.status)}`}>
                              {item.status}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 truncate">{item.description}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-[10px] text-gray-500">{item.customer_name}</p>
                            <p className="text-[10px] text-gray-500">{formatTimeAgo(item.created_at)}</p>
                          </div>
                          {item.amount > 0 && (
                            <p className="text-xs text-yellow-500 font-medium mt-1">
                              {formatCurrency(item.amount)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <TrendingUp className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No recent activity</p>
                    <p className="text-xs text-gray-600">Bookings and inquiries will appear here</p>
                  </div>
                )}
              </div>
            
              {/* Business List */}
              <div className="p-4">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Switch Business</p>
                <div className="space-y-2">
                  {businesses && businesses.map((biz) => (
                    <button
                      key={biz.business_id}
                      onClick={() => handleSwitchBusiness(biz.business_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        currentBusiness?.business_id === biz.business_id 
                          ? 'bg-yellow-500/20 border border-yellow-500/50' 
                          : 'bg-gray-700/50 hover:bg-gray-700'
                      }`}
                      data-testid={`mobile-biz-${biz.business_id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-yellow-500" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white font-medium truncate">{biz.business_name}</p>
                        <p className={`text-xs ${
                          biz.verification_status === 'verified' ? 'text-green-400' : 'text-yellow-400'
                        }`}>
                          {biz.verification_status === 'verified' ? 'Verified' : 'Pending'}
                        </p>
                      </div>
                      {currentBusiness?.business_id === biz.business_id && (
                        <Check className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                  
                  {/* Add Business Button */}
                  <button
                    onClick={() => {
                      setShowBusinessSheet(false);
                      navigate('/become-vendor');
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-700/30 hover:bg-gray-700/50 border border-dashed border-gray-600"
                    data-testid="mobile-add-business-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                      <Plus className="h-5 w-5 text-yellow-500" />
                    </div>
                    <p className="text-yellow-500 font-medium">Add Another Business</p>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowBusinessSheet(false);
                  navigate('/business/dashboard');
                }}
                className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-colors"
                data-testid="mobile-goto-dashboard-btn"
              >
                Go to Business Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 z-50 safe-area-bottom">
        <div className="flex justify-around items-center h-16">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors ${
                  active ? 'text-yellow-500' : 'text-gray-400'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <div className="relative">
                  <Icon className={`h-5 w-5 ${active ? 'text-yellow-500' : 'text-gray-400'}`} />
                  {item.badge && (
                    <Badge className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 bg-yellow-500 text-black text-[10px] font-bold">
                      {item.badge > 9 ? '9+' : item.badge}
                    </Badge>
                  )}
                </div>
                <span className={`text-[10px] mt-1 ${active ? 'text-yellow-500 font-medium' : 'text-gray-400'}`}>
                  {item.label}
                </span>
                {active && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-yellow-500 rounded-full" />
                )}
              </button>
            );
          })}

          {/* Business/Account Button */}
          {user && hasBusiness ? (
            <button
              onClick={() => setShowBusinessSheet(true)}
              className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors ${
                location.pathname.startsWith('/business') ? 'text-yellow-500' : 'text-gray-400'
              }`}
              data-testid="mobile-nav-business"
            >
              <div className="relative">
                <Building2 className={`h-5 w-5 ${location.pathname.startsWith('/business') ? 'text-yellow-500' : 'text-gray-400'}`} />
                <ChevronUp className="absolute -top-1 -right-2 h-3 w-3 text-yellow-500" />
                {stats && stats.pending_bookings > 0 && (
                  <Badge className="absolute -top-2 -right-3 h-4 w-4 flex items-center justify-center p-0 bg-red-500 text-white text-[10px] font-bold">
                    {stats.pending_bookings > 9 ? '9+' : stats.pending_bookings}
                  </Badge>
                )}
              </div>
              <span className={`text-[10px] mt-1 truncate max-w-[60px] ${location.pathname.startsWith('/business') ? 'text-yellow-500 font-medium' : 'text-gray-400'}`}>
                {currentBusiness?.business_name?.split(' ')[0] || 'Business'}
              </span>
              {location.pathname.startsWith('/business') && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-yellow-500 rounded-full" />
              )}
            </button>
          ) : (
            <button
              onClick={() => navigate(user ? '/my-bookings' : '/login')}
              className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors ${
                isActive(user ? '/my-bookings' : '/login') ? 'text-yellow-500' : 'text-gray-400'
              }`}
              data-testid="mobile-nav-account"
            >
              <User className={`h-5 w-5 ${isActive(user ? '/my-bookings' : '/login') ? 'text-yellow-500' : 'text-gray-400'}`} />
              <span className={`text-[10px] mt-1 ${isActive(user ? '/my-bookings' : '/login') ? 'text-yellow-500 font-medium' : 'text-gray-400'}`}>
                {user ? 'Account' : 'Login'}
              </span>
              {isActive(user ? '/my-bookings' : '/login') && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-yellow-500 rounded-full" />
              )}
            </button>
          )}
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
